import {
    Box,
    Grid,
    Toolbar,
    Typography,
    CircularProgress,
    Button,
    Pagination,
    TextField,
    Card,
    CardContent,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Stack,
    Chip,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import {
    CloudDownloadOutlined,
    KeyboardArrowLeftOutlined,
} from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import { useNavigate } from "react-router-dom";

const InventoryReport = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

    const [loading, setLoading] = useState(false);
    const [date, setDate] = useState(new Date());
    const [reportRows, setReportRows] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedMaterial, setSelectedMaterial] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 50;

    const getInventoryReport = async (download = false) => {
        if (!outletData?._id) return;

        setLoading(true);

        try {
            const formattedDate = format(date, "yyyy-MM-dd");

            const queryParams = new URLSearchParams();
            queryParams.append("outlet", outletData?._id);
            queryParams.append("date", formattedDate);

            if (download) {
                queryParams.append("isDownload", "true");
            }

            const response = await axios.get(
                `${API.BASE_URL}stock-transactions/outletWiseRawMaterialStockReport?${queryParams.toString()}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: download ? "blob" : "json",
                }
            );

            if (download) {
                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);

                const selectedOutletName =
                    outletData?.name || outletData?.outletName || "Outlet";
                link.download = `${selectedOutletName} Inventory Report (${formattedDate}).xlsx`;

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);

                toast.success("Report Excel file downloaded successfully!", {
                    position: "bottom-center",
                    autoClose: 3000,
                });
                return;
            }

            const resData = response?.data;

            if (!resData?.status) {
                setReportRows([]);
                toast.info(resData?.data?.message || "No data found", {
                    autoClose: 2500,
                });
                return;
            }

            const resultRows = Array.isArray(resData?.data?.result)
                ? resData.data.result
                : [];

            setReportRows(resultRows);
            setCurrentPage(1);

            toast.success(
                resData?.data?.message ||
                "Inventory report fetched successfully.",
                { autoClose: 2000 }
            );
        } catch (error) {
            console.error("Error fetching report:", error);
            if (!download) setReportRows([]);
            toast.error(
                download ? "Failed to download report" : "Failed to fetch report",
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    };

    const materialOptions = useMemo(() => {
        const map = new Map();

        (reportRows || []).forEach((item) => {
            if (item?.rawMaterial && !map.has(item.rawMaterial)) {
                map.set(item.rawMaterial, {
                    id: item.rawMaterial,
                    name: item.rawMaterialName || "Unnamed Material",
                });
            }
        });

        return Array.from(map.values());
    }, [reportRows]);

    const filteredRows = useMemo(() => {
        const search = searchText.trim().toLowerCase();

        return (reportRows || []).filter((row) => {
            const matchesMaterial =
                !selectedMaterial || row?.rawMaterial === selectedMaterial;

            const searchableText = [
                row?.rawMaterialName,
                row?.unit,
                String(row?.openingStock ?? ""),
                String(row?.received ?? ""),
                String(row?.consumed ?? ""),
                String(row?.wastage ?? ""),
                String(row?.closingStock ?? ""),
            ]
                .join(" ")
                .toLowerCase();

            const matchesSearch = !search || searchableText.includes(search);

            return matchesMaterial && matchesSearch;
        });
    }, [reportRows, selectedMaterial, searchText]);

    const totalPages = Math.ceil(filteredRows.length / itemsPerPage) || 1;

    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredRows.slice(start, start + itemsPerPage);
    }, [filteredRows, currentPage]);

    const unitTotals = useMemo(() => {
        const result = {
            received: {},
            consumed: {},
            wastage: {},
        };

        (filteredRows || []).forEach((row) => {
            const unit = row?.unit || "NA";

            // RECEIVED
            result.received[unit] =
                (result.received[unit] || 0) + Number(row?.received || 0);

            // CONSUMED
            result.consumed[unit] =
                (result.consumed[unit] || 0) + Number(row?.consumed || 0);

            // WASTAGE
            result.wastage[unit] =
                (result.wastage[unit] || 0) + Number(row?.wastage || 0);
        });

        return result;
    }, [filteredRows]);

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    useEffect(() => {
        getInventoryReport(false);
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, selectedMaterial]);

    return (
        <Box
            className="dashBoard"
            sx={{ background: "#f6f8fb", minHeight: "100vh" }}
        >
            <Toolbar />

            <Grid container justifyContent="center" sx={{ pb: 6 }}>
                <Grid item xs={12} md={11}>
                    <Card
                        elevation={0}
                        sx={{
                            mt: 4,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                        }}
                    >
                        <CardContent sx={{ py: 3 }}>
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                alignItems={{ xs: "flex-start", md: "center" }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                    <KeyboardArrowLeftOutlined
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => navigate(-1)}
                                        fontSize="large"
                                    />

                                    <Box>
                                        <Typography
                                            sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}
                                        >
                                            Outlet Wise Raw Material Stock Report
                                        </Typography>
                                        <Typography
                                            sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}
                                        >
                                            View opening stock, received, consumed, wastage and
                                            closing stock for each raw material.
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Chip
                                    icon={<FilterAltOutlined />}
                                    label={`Records: ${filteredRows.length}`}
                                    sx={{ fontWeight: 700, borderRadius: 2 }}
                                    variant="outlined"
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            background: "#fff",
                        }}
                    >
                        <CardContent sx={{ py: 2 }}>
                            <Grid container spacing={2} alignItems="end">
                                <Grid item xs={12} md={2.5}>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: "#334155",
                                            mb: 0.8,
                                        }}
                                    >
                                        Select Date
                                    </Typography>

                                    <DatePicker
                                        selected={date}
                                        onChange={(selectedDate) => setDate(selectedDate)}
                                        dateFormat="yyyy-MM-dd"
                                        showYearDropdown
                                        scrollableYearDropdown
                                        yearDropdownItemNumber={100}
                                        customInput={
                                            <TextField
                                                fullWidth
                                                value={date ? format(date, "yyyy-MM-dd") : ""}
                                                InputProps={{ readOnly: true }}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: 2,
                                                        height: 52,
                                                        background: "#fff",
                                                    },
                                                }}
                                            />
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: "#334155",
                                            mb: 0.8,
                                        }}
                                    >
                                        Raw Material
                                    </Typography>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                height: 52,
                                            },
                                        }}
                                    >
                                        <Select
                                            value={selectedMaterial}
                                            onChange={(e) => setSelectedMaterial(e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value="">
                                                <em>All</em>
                                            </MenuItem>

                                            {materialOptions.map((item) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={3.5}>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: "#334155",
                                            mb: 0.8,
                                        }}
                                    >
                                        Search
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        placeholder="Search by raw material, unit or stock values"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 2,
                                                height: 52,
                                                background: "#fff",
                                            },
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchRounded />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: "#334155",
                                            mb: 0.8,
                                            textAlign: "center",
                                        }}
                                    >
                                        Actions
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        spacing={1}
                                    >
                                        <Button
                                            variant="contained"
                                            disabled={loading}
                                            onClick={() => getInventoryReport(false)}
                                            sx={{
                                                height: 52,
                                                width: "70%",
                                                borderRadius: 2,
                                                fontWeight: 800,
                                                textTransform: "none",
                                                backgroundColor: "#003049",
                                                "&:hover": { backgroundColor: "#00263a" },
                                            }}
                                        >
                                            {loading ? "Loading..." : "Get"}
                                        </Button>

                                        <CloudDownloadOutlined
                                            onClick={() => {
                                                if (!filteredRows.length || loading) return;
                                                getInventoryReport(true);
                                            }}
                                            sx={{
                                                fontSize: "35px",
                                                cursor:
                                                    filteredRows.length && !loading
                                                        ? "pointer"
                                                        : "not-allowed",
                                                color:
                                                    filteredRows.length && !loading
                                                        ? "#003049"
                                                        : "#94a3b8",
                                                display: "flex",
                                                alignSelf: "center",
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {[
                            {
                                label: "Received",
                                values: unitTotals.received,
                            },
                            {
                                label: "Consumed",
                                values: unitTotals.consumed,
                            },
                            {
                                label: "Wastage",
                                values: unitTotals.wastage,
                            },
                        ].map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.label}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        border: "1px solid #e6eaf0",
                                        background: "#fff",
                                        height: "100%",
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: "#64748b",
                                                fontWeight: 700,
                                                mb: 1,
                                            }}
                                        >
                                            {item.label}
                                        </Typography>

                                        {Object.keys(item.values || {}).length > 0 ? (
                                            Object.entries(item.values).map(([unit, value]) => (
                                                <Typography
                                                    key={unit}
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 800,
                                                        color: "#0f172a",
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    {unit} → {Number(value || 0).toFixed(2)}
                                                </Typography>
                                            ))
                                        ) : (
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                    color: "#94a3b8",
                                                }}
                                            >
                                                No data
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid> */}

                    <Card
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            overflow: "hidden",
                            background: "#fff",
                        }}
                    >
                        <CardContent sx={{ p: 0 }}>
                            {loading ? (
                                <Box
                                    sx={{
                                        py: 6,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 2,
                                    }}
                                >
                                    <CircularProgress />
                                    <Typography sx={{ color: "#334155", fontWeight: 700 }}>
                                        Fetching Report...
                                    </Typography>
                                </Box>
                            ) : filteredRows.length === 0 ? (
                                <Box sx={{ py: 6, textAlign: "center" }}>
                                    <Typography
                                        sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}
                                    >
                                        No inventory to display
                                    </Typography>
                                    <Typography
                                        sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}
                                    >
                                        Select filters and click “Get” to view results.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            borderBottom: "1px solid #e6eaf0",
                                            background: "#fff",
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="space-between"
                                            useFlexGap
                                            flexWrap="wrap"
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                useFlexGap
                                                flexWrap="wrap"
                                            >
                                                <Chip
                                                    icon={<FilterAltOutlined />}
                                                    label={`Rows: ${filteredRows.length}`}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                />
                                                <Chip
                                                    label={`Date: ${format(date, "yyyy-MM-dd")}`}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                />
                                            </Stack>

                                            <Chip
                                                label={outletData?.name || "Outlet"}
                                                sx={{
                                                    fontWeight: 900,
                                                    borderRadius: 2,
                                                    backgroundColor: "#003049",
                                                    color: "#fff",
                                                    "& .MuiChip-label": { color: "#fff" },
                                                }}
                                            />
                                        </Stack>
                                    </Box>

                                    <TableContainer
                                        component={Paper}
                                        elevation={0}
                                        sx={{
                                            borderRadius: 0,
                                            maxHeight: "70vh",
                                            overflow: "auto",
                                        }}
                                    >
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {[
                                                        "No.",
                                                        "Raw Material",
                                                        "Unit",
                                                        "Opening Stock",
                                                        "Received",
                                                        "Consumed",
                                                        "Wastage",
                                                        "Closing Stock",
                                                    ].map((header) => (
                                                        <TableCell
                                                            key={header}
                                                            align={header === "Raw Material" ? "left" : "center"}
                                                            sx={{
                                                                fontWeight: 900,
                                                                color: "#0f172a",
                                                                background: "#f1f5f9",
                                                                borderBottom: "1px solid #e2e8f0",
                                                                whiteSpace: "nowrap",
                                                                position: "sticky",
                                                                top: 0,
                                                                zIndex: 2,
                                                            }}
                                                        >
                                                            {header}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {paginatedRows.map((row, index) => (
                                                    <TableRow
                                                        key={row?.rawMaterial || index}
                                                        hover
                                                        sx={{
                                                            backgroundColor:
                                                                index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                                            "& td": {
                                                                borderBottom: "1px solid #f1f5f9",
                                                            },
                                                        }}
                                                    >
                                                        <TableCell
                                                            align="center"
                                                            sx={{ fontWeight: 700, color: "#334155" }}
                                                        >
                                                            {(currentPage - 1) * itemsPerPage + index + 1}
                                                        </TableCell>

                                                        <TableCell align="left">
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 14,
                                                                    fontWeight: 900,
                                                                    color: "#0f172a",
                                                                }}
                                                            >
                                                                {row?.rawMaterialName || "N/A"}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell
                                                            align="center"
                                                            sx={{ fontWeight: 700, color: "#334155" }}
                                                        >
                                                            {row?.unit || "-"}
                                                        </TableCell>

                                                        <TableCell
                                                            align="center"
                                                            sx={{ fontWeight: 800, color: "#0f172a" }}
                                                        >
                                                            {Number(row?.openingStock || 0).toFixed(2)}
                                                        </TableCell>

                                                        <TableCell
                                                            align="center"
                                                            sx={{ fontWeight: 800, color: "#0f172a" }}
                                                        >
                                                            {Number(row?.received || 0).toFixed(2)}
                                                        </TableCell>

                                                        <TableCell
                                                            align="center"
                                                            sx={{ fontWeight: 800, color: "#0f172a" }}
                                                        >
                                                            {Number(row?.consumed || 0).toFixed(2)}
                                                        </TableCell>

                                                        <TableCell
                                                            align="center"
                                                            sx={{ fontWeight: 800, color: "#0f172a" }}
                                                        >
                                                            {Number(row?.wastage || 0).toFixed(2)}
                                                        </TableCell>

                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                fontWeight: 900,
                                                                color:
                                                                    Number(row?.closingStock || 0) < 0
                                                                        ? "#dc2626"
                                                                        : "#0f172a",
                                                            }}
                                                        >
                                                            {Number(row?.closingStock || 0).toFixed(2)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Divider />

                                    <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                                        <Pagination
                                            count={totalPages}
                                            page={currentPage}
                                            onChange={handlePageChange}
                                            color="primary"
                                            variant="outlined"
                                            shape="rounded"
                                        />
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default InventoryReport;